const PROXY_URL = '';

export const studioApiService = {
  async getStatus() {
    const res = await fetch(`${PROXY_URL}/status`);
    if (!res.ok) throw new Error('Proxy server not responding');
    return res.json();
  },

  async generateImages(apiKey: string, prompt: string, garmentType: string, quality: string, size: string, images: { file: File, previewUrl: string }[]) {
    const formData = new FormData();
    formData.append('prompt', prompt);
    formData.append('garmentType', garmentType);
    formData.append('quality', quality);
    formData.append('size', size);
    
    images.forEach((img, i) => {
      if (i === 0) {
        formData.append('image', img.file);
      } else {
        formData.append(`image_${i}`, img.file);
      }
    });

    const headers: HeadersInit = {};
    if (apiKey) {
      headers['x-openai-key'] = apiKey;
    }

    const res = await fetch(`${PROXY_URL}/generate`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Generation failed' }));
      throw new Error(err.error || 'Generation failed');
    }
    return res.json();
  },

  async upscaleImage(apiKey: string, imageBase64: string) {
    const res = await fetch(`${PROXY_URL}/upscale`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiKey, imageBase64 })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upscale failed' }));
      throw new Error(err.error || 'Upscale failed');
    }
    return res.json();
  },

  async pollUpscaleStatus(apiKey: string, predictionId: string) {
    const res = await fetch(`${PROXY_URL}/poll/${predictionId}`, {
      headers: {
        'Authorization': `Bearer ${apiKey}`
      }
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Polling failed' }));
      throw new Error(err.error || 'Polling failed');
    }
    return res.json();
  },

  async uploadToS3(rowId: string, base64Images: string[]) {
    const res = await fetch(`${PROXY_URL}/upload-s3`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ row_id: rowId, images: base64Images })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'S3 Upload failed' }));
      throw new Error(err.error || 'S3 Upload failed');
    }
    return res.json();
  },

  async fetchS3ImagesByRowId(rowId: string) {
    const res = await fetch(`${PROXY_URL}/s3-images/${rowId}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to fetch S3 images' }));
      throw new Error(err.error || 'Failed to fetch S3 images');
    }
    return res.json();
  },

  async createProductInDB(payload: any) {
    try {
      const res = await fetch(`${PROXY_URL}/create-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const bodyText = await res.text();
      let parsedBody: any;
      try { parsedBody = JSON.parse(bodyText); } catch(e) {}

      // Handle 409 Conflict / "already exists": Django automatically updates existing products on POST
      if (res.status === 409 || parsedBody?.message?.includes('already exists')) {
        return { success: true, updated: true, id: parsedBody?.id, message: parsedBody?.message || 'Updated existing product' };
      }

      if (!res.ok) {
        const errorMsg = parsedBody?.message || parsedBody?.error || (typeof parsedBody === 'object' ? JSON.stringify(parsedBody) : bodyText) || 'Database Upload failed';
        throw new Error(errorMsg);
      }
      return parsedBody || {};
    } catch (e: any) {
      if (e.message && !e.message.includes('fetch')) {
        throw e;
      }
      console.warn('Proxy POST failed, attempting direct Django DB fetch...');
    }

    // Direct fallback to Django backend API
    const djangoRes = await fetch(`http://127.0.0.1:8000/api/myntra/products/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const bodyText = await djangoRes.text();
    let parsedBody: any;
    try { parsedBody = JSON.parse(bodyText); } catch(e) {}

    // Handle 409 Conflict / "already exists": Django automatically updates existing products on POST
    if (djangoRes.status === 409 || parsedBody?.message?.includes('already exists')) {
      return { success: true, updated: true, id: parsedBody?.id, message: parsedBody?.message || 'Updated existing product' };
    }

    if (!djangoRes.ok) {
      const errorMsg = parsedBody?.message || parsedBody?.error || (typeof parsedBody === 'object' ? JSON.stringify(parsedBody) : bodyText) || 'Database Upload failed';
      throw new Error(errorMsg);
    }
    return parsedBody || {};
  },

  async linkRelatedProducts(productIds: number[]) {
    const res = await fetch(`${PROXY_URL}/link-related-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_ids: productIds })
    });
    const data = await res.json().catch(() => ({ success: false }));
    return data;
  },

  async updateProductInDB(existingId: number | string, payload: any) {
    try {
      const res = await fetch(`${PROXY_URL}/update-product/${existingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const bodyText = await res.text();
      let parsedBody: any;
      try { parsedBody = JSON.parse(bodyText); } catch(e) {}

      if (res.ok) return parsedBody || {};
    } catch (e: any) {
      console.warn('Proxy PATCH failed, attempting direct Django DB update...');
    }

    // Direct fallback to Django PATCH endpoint
    const djangoRes = await fetch(`http://127.0.0.1:8000/api/myntra/products/${existingId}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const bodyText = await djangoRes.text();
    let parsedBody: any;
    try { parsedBody = JSON.parse(bodyText); } catch(e) {}

    if (!djangoRes.ok) {
      const errorMsg = parsedBody?.message || parsedBody?.error || (typeof parsedBody === 'object' ? JSON.stringify(parsedBody) : bodyText) || 'Database Update failed';
      throw new Error(errorMsg);
    }
    return parsedBody || {};
  },

  async syncGoogleSheet() {
    const res = await fetch(`${PROXY_URL}/sync-google-sheet`);
    const data = await res.json().catch(() => ({ success: false, error: 'Network error contacting proxy' }));
    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Failed to sync products from Google Sheet');
    }
    return data;
  },

  async updateSheetProductIds(updates: { row_id: string; product_id: string }[]) {
    const res = await fetch(`${PROXY_URL}/update-sheet-product-ids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    const data = await res.json().catch(() => ({ success: false, error: 'Network error' }));
    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Failed to update Google Sheet product IDs');
    }
    return data;
  },

  async markUnavailableByBrand(brand: string = 'Slayd Originals', marketplace: string = 'Slayd') {
    const res = await fetch(`${PROXY_URL}/mark-unavailable-by-brand`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ brand, marketplace })
    });
    const data = await res.json().catch(() => ({}));
    return data;
  }
};


